import type { GalleryImageT } from '../../../../../type/galleryImageT';
import type { GalleryMediaT } from '../../../../../type/galleryMediaT';
import type { GalleryVideoT } from '../../../../../type/galleryVideo';

import {
  ProductGalleryThumbBar,
  ProductGalleryThumbGrid,
  ProductGalleryVariant,
} from '@vs-shop/commerce-ui/productGallery';

import { GalleryThumbExtra } from './GalleryThumbExtra';
import { GalleryThumbImage } from './GalleryThumbImage';
import { GalleryThumbVideo } from './GalleryThumbVideo';

type Props = {
  images: GalleryImageT[];
  videos: GalleryVideoT[];
  mediaSortedByPosition: GalleryMediaT[];
  selectedIdx: number;
  onThumb: (idx: number) => void;
  onShowAll: () => void;
  onShowVideo: (idx: number) => void;
  mediaShown: number;
  alt: string;
  firstVideoIndex: number;
};

/**
 * What rcprofi puts in the thumb rail: as many photographs as fit, then a cell
 * counting the rest, then a cell for the videos.
 *
 * The cap is this tenant's, and so is resolving a photograph's place in the rail
 * back to its index in the combined, position-sorted media — the rail is ordered
 * by the photographs alone, while the main view steps through photographs and
 * videos together.
 */
export function GalleryThumbBar({
  images,
  videos,
  mediaSortedByPosition,
  selectedIdx,
  onThumb,
  onShowAll,
  onShowVideo,
  mediaShown,
  alt,
  firstVideoIndex,
}: Props) {
  const extra = images.length - mediaShown;

  return (
    <ProductGalleryThumbBar variant={ProductGalleryVariant.INLINE}>
      <ProductGalleryThumbGrid variant={ProductGalleryVariant.INLINE}>
        {images.slice(0, mediaShown).map((img) => {
          const actualIdx = mediaSortedByPosition.findIndex(
            (media) => media.mediaType === 'image' && media.pathAbstract === img.pathAbstract
          );

          return (
            <GalleryThumbImage
              key={img.pathAbstract}
              image={img}
              selected={selectedIdx === actualIdx}
              onClick={() => {
                onThumb(actualIdx);
              }}
              alt={alt}
            />
          );
        })}
        {extra > 0 && <GalleryThumbExtra count={extra} onClick={onShowAll} />}
        {videos.length > 0 && (
          <GalleryThumbVideo
            count={videos.length}
            onClick={() => {
              onShowVideo(firstVideoIndex);
            }}
          />
        )}
      </ProductGalleryThumbGrid>
    </ProductGalleryThumbBar>
  );
}
